import EntityBaseRepository from '@/api/misc/EntityBaseRepository';
import { AxiosResponse } from 'axios';

export interface SmsLogFilters {
    page?: number,
    pageSize?: number,
    status?: string,
    purpose?: string,
    phone?: string,
    from?: string,
    to?: string
}

/**
 * Repository for the admin SMS monitor endpoints (backend `sms` service).
 */
export default class SmsRepository extends EntityBaseRepository {
    public getLogs(filters: SmsLogFilters = {}): Promise<AxiosResponse> {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        const query = params.toString();
        return this.axiosClient.get(`sms/logs${query ? `?${query}` : ''}`);
    }

    public getStats(from?: string, to?: string): Promise<AxiosResponse> {
        const params = new URLSearchParams();
        if (from) {
            params.append('from', from);
        }
        if (to) {
            params.append('to', to);
        }
        const query = params.toString();
        return this.axiosClient.get(`sms/stats${query ? `?${query}` : ''}`);
    }

    public getAccountStatus(): Promise<AxiosResponse> {
        return this.axiosClient.get('sms/account');
    }

    public resend(phone: { prefix: string, phoneNumber: string }, type: string): Promise<AxiosResponse> {
        return this.axiosClient.post('sms/resend', { phone, type });
    }

    /**
     * Trigger the backend to (re)send the phone's current OTP over WhatsApp.
     * Backend: POST /api/sms/whatsapp { phone, type }. Requires Vonage WhatsApp config.
     */
    public sendWhatsapp(phone: { prefix: string, phoneNumber: string }, type: string): Promise<AxiosResponse> {
        return this.axiosClient.post('sms/whatsapp', { phone, type });
    }

    /**
     * Admin: issue a one-time support login code for ONE account (replaces the
     * removed global super OTP). The response carries the code ONCE:
     * { code, expiresAt, issuedCodeId, accountType, accountId }.
     * 404 = no such account; 422 = blocked, or driver/company inactive.
     */
    public issueLoginCode(type: 'CUSTOMER' | 'DRIVER', phone: { prefix: string, phoneNumber: string },
        reason: string): Promise<AxiosResponse> {
        const path = type === 'DRIVER' ? 'sms/login-code/driver' : 'sms/login-code/customer';
        return this.axiosClient.post(path, { phone, reason });
    }

    /**
     * Admin: the phone's recent support codes (LIVE / USED / DEAD / EXPIRED, who
     * issued each and why) and this hour's login budgets. Never a code.
     */
    public getLoginCodeStatus(type: 'CUSTOMER' | 'DRIVER',
        phone: { prefix: string, phoneNumber: string }): Promise<AxiosResponse> {
        const params = new URLSearchParams({ prefix: phone.prefix, phoneNumber: phone.phoneNumber, type });
        return this.axiosClient.get(`sms/login-code?${params.toString()}`);
    }
}
